/**
 * CommandPalette — Linear-style Cmd+K (or Ctrl+K) overlay.
 *
 * Opens a centered Radix Dialog with a search input that filters
 * across the 24 segments and the 53 Municipal Mirror products.
 * Selecting a row navigates to the matching detail surface.
 *
 * Per [docs/DASHBOARD-HOME-EXPLORE-IMPLEMENTATION.md §3.5].
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Search } from 'lucide-react';

import { SEGMENTS } from '@/data/segments';
import { arrive } from '@/lib/motion';

interface PaletteItem {
  id: string;
  label: string;
  hint: string;
  to: string;
}

export function CommandPalette() {
  const { t } = useTranslation('marketplace');
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Cmd+K / Ctrl+K to open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const items = useMemo<PaletteItem[]>(() => {
    const segmentItems: PaletteItem[] = SEGMENTS.map((s) => ({
      id: `seg:${s.id}`,
      label: t(`segments:segments.${s.id}.name`, { defaultValue: s.id }),
      hint: t('explore.palette_segment'),
      to: `https://growing-intelligence.com/${s.id}`,
    }));
    const q = query.trim().toLowerCase();
    if (!q) return segmentItems.slice(0, 12);
    return segmentItems
      .filter(
        (it) =>
          it.label.toLowerCase().includes(q) ||
          it.id.toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [query, t]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <AnimatePresence>
          {open ? (
            <>
              <Dialog.Overlay asChild forceMount>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
                />
              </Dialog.Overlay>
              <Dialog.Content asChild forceMount>
                {/* Viewport-relative centering: `left-1/2 -translate-x-1/2`
                    is screen-space geometry, not text flow. The palette must
                    sit at the physical centre of the viewport in both LTR
                    and RTL — logical `start-1/2` would shift it to the
                    trailing edge under RTL. Allowlisted in rtl.spec.ts. */}
                <motion.div
                  className="fixed left-1/2 top-[20%] z-50 w-[640px] max-w-[calc(100vw-2rem)] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-surface shadow-raised"
                  {...(reduce
                    ? {}
                    : {
                        variants: arrive,
                        initial: 'hidden',
                        animate: 'visible',
                        exit: 'hidden',
                      })}
                >
                  <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                    <Search className="h-4 w-4 text-ink-muted" aria-hidden />
                    <input
                      autoFocus
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder={t('explore.palette_placeholder')}
                      className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-muted"
                    />
                    <kbd className="rounded border border-border px-1.5 py-0.5 font-mono text-[11px] text-ink-muted">
                      Esc
                    </kbd>
                  </div>
                  <ul className="max-h-80 overflow-y-auto p-2">
                    {items.length === 0 ? (
                      <li className="px-3 py-4 text-center text-sm text-ink-muted">
                        {t('explore.palette_empty')}
                      </li>
                    ) : (
                      items.map((it) => (
                        <li key={it.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setOpen(false);
                              setQuery('');
                              // Mirror items and segment items now point to
                              // the public website; use window.open for
                              // external URLs, navigate for internal paths.
                              if (it.to.startsWith('http')) {
                                window.open(it.to, '_blank', 'noopener,noreferrer');
                              } else {
                                navigate(it.to);
                              }
                            }}
                            className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-start hover:bg-spark-soft focus-visible:bg-spark-soft focus-visible:outline-none"
                          >
                            <span className="truncate text-sm font-medium text-ink">
                              {it.label}
                            </span>
                            <span className="shrink-0 text-xs text-ink-muted">
                              {it.hint}
                            </span>
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </motion.div>
              </Dialog.Content>
            </>
          ) : null}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
